import {
  Cartesian2,
  Cartesian3,
  Ellipsoid,
  EllipsoidSurfaceAppearance,
  GeometryInstance,
  Material,
  Primitive,
  GroundPrimitive,
  Rectangle,
  RectangleGeometry,
} from 'cesium';

const sourceR = `
czm_material czm_getMaterial(czm_materialInput materialInput) {
czm_material material = czm_getDefaultMaterial(materialInput);
float r = texture2D(image, materialInput.st).r;
vec4 color = texture2D(color_ramp, vec2(r, 0.5));
color = czm_gammaCorrect(color);
material.diffuse = color.rgb;
material.alpha = color.a;
return material;
}
`;
const sourceUV = `
uniform vec3 u_range_uv;
uniform vec3 u_range_s;

float uv2s(vec4 rgba){
  float u = rgba.r * (u_range_uv.y - u_range_uv.x) + u_range_uv.x;
  float v = rgba.g * (u_range_uv.y - u_range_uv.x) + u_range_uv.x;
  float s = sqrt(u * u + v * v);
  return (s - u_range_s.x) / (u_range_s.y - u_range_s.x);
}

czm_material czm_getMaterial(czm_materialInput materialInput) {
  czm_material material = czm_getDefaultMaterial(materialInput);
  vec4 rgba = texture2D(image, materialInput.st);
  float r = uv2s(rgba);
  vec4 color = texture2D(color_ramp, vec2(r, 0.5));
  color = czm_gammaCorrect(color);
  material.diffuse = color.rgb;
  material.alpha = color.a;
  return material;
}
`;

export const ColorConfig = {
  wind: [
    [0, 'rgb(98, 113, 184)'], // 0
    [12 / 255, 'rgb(61, 110, 163)'], // 1.5m/s
    [24 / 255, 'rgb(74, 148, 170)'], // 3m/s
    [32 / 255, 'rgb(74, 146, 148)'], // 4m/s
    [40 / 255, 'rgb(77, 142, 124)'], // 5m/s
    [60 / 255, 'rgb(76, 164, 76)'], // 7.5m/s
    [80 / 255, 'rgb(103, 164, 54)'], // 10m/s
    [100 / 255, 'rgb(162, 135, 64)'], // 12.5m/s
    [120 / 255, 'rgb(162, 109, 92)'], // 15m/s
    [140 / 255, 'rgb(141, 63, 92)'], // 17.5m/s
    [160 / 255, 'rgb(151, 75, 145)'], // 20m/s
    [200 / 255, 'rgb(95, 100, 160)'], // 25m/s
    [240 / 255, 'rgb(91, 136, 161)'], // 30m/s
    [1, 'rgb(91, 136, 161)'],
  ],
  seaCurrent: [
    [0, 'rgba(64, 77, 144,0)'], // 0
    [10 / 255, 'rgb(61, 121, 110)'], // 0.1
    [20 / 255, 'rgb(50, 140, 50)'], // 0.2
    [30 / 255, 'rgb(140, 133, 49)'], // 0.3
    [40 / 255, 'rgb(143, 115, 50)'], // 0.4
    [60 / 255, 'rgb(117, 52, 68)'], // 0.6
    [80 / 255, 'rgb(107, 67, 131)'], // 0.8
    [90 / 255, 'rgb(67, 93, 133)'], // 0.9
    [100 / 255, 'rgb(73, 122, 132)'], // 1
    [130 / 255, 'rgb(115, 135, 139)'], // 1.3
    [160 / 255, 'rgb(144, 144, 144)'], // 1.6
    [1, 'rgba(205, 215, 220, 0)'],
  ],
  waves: [
    // 跟气导平台配色一致
    [0, 'rgba(144,144,144, 0)'],
    [1 / 255, 'rgb(50,158,186)'],
    [20 / 255, 'rgb(48,99,142)'], // 1
    [40 / 255, 'rgb(57, 61, 143)'], // 2
    [80 / 255, 'rgb(205, 91, 209)'], // 4
    [120 / 255, 'rgb(146, 15, 92)'], // 6
    [160 / 255, 'rgb(163, 44, 8)'], // 8
    [200 / 255, 'rgb(128, 13, 23)'], // 10
    [240 / 255, 'rgb(30, 3, 4)'], // 12
    [254 / 255, 'rgb(30, 3, 4)'],
    [1, 'rgb(30, 3, 4)'],
  ],
};

export const RangeConfig = {
  windUV: [-25, 0.2],
  windS: [0, 0.125],
  seaCurrentUV: [-2.5, 0.02],
  seaCurrentS: [0, 0.01],
  wavesS: [0, 0.2083],
};

export const Factor = {
  Waves: 'waves',
  Wind: 'wind',
  SeaCurrents: 'seaCurrents',
};

export function useColorMap(options) {
  const { url, factor } = options;

  const rectangle = new RectangleGeometry({
    ellipsoid: Ellipsoid.WGS84,
    height: 0,
    rectangle: Rectangle.fromDegrees(-180.0, -90, 180, 90),
    vertexFormat: EllipsoidSurfaceAppearance.VERTEX_FORMAT,
  });
  const geometry = new GeometryInstance({
    geometry: rectangle,
  });

  let colorArray = [];
  let range = [];
  const fabric = {
    uniforms: {
      image: url,
      color_ramp: null,
    },
    source: '',
  };
  const rangeUV = rangeFormat(RangeConfig.windUV);
  const rangeUV2 = rangeFormat(RangeConfig.seaCurrentUV);
  switch (factor) {
    case Factor.Waves:
      colorArray = ColorConfig.waves;
      range = rangeFormat(RangeConfig.wavesS);
      fabric.source = sourceR;
      break;
    case Factor.Wind:
      colorArray = ColorConfig.wind;
      range = rangeFormat(RangeConfig.windS);
      fabric.source = sourceUV;
      fabric.uniforms.u_range_uv = new Cartesian3(
        rangeUV[0],
        rangeUV[1],
        rangeUV[2]
      );
      fabric.uniforms.u_range_s = new Cartesian3(range[0], range[1], range[2]);
      break;
    case Factor.SeaCurrents:
      colorArray = ColorConfig.seaCurrent;
      range = rangeFormat(RangeConfig.seaCurrentS);
      fabric.source = sourceUV;
      fabric.uniforms.u_range_uv = new Cartesian3(
        rangeUV2[0],
        rangeUV2[1],
        rangeUV2[2]
      );
      fabric.uniforms.u_range_s = new Cartesian3(range[0], range[1], range[2]);
      break;
  }

  const [min, max, delta] = range;
  const colors = colorArray.map((item) => [
    item[0] * 255 * delta + min,
    item[1],
  ]);
  const colorOptions = {
    colorScaleType: '',
  };
  const color = createColorTexture([], colors, colorOptions);
  fabric.uniforms.color_ramp = color.canvas;
  if(options.mapType === '3d') {
    return new GroundPrimitive({
      geometryInstances: geometry,
      appearance: new EllipsoidSurfaceAppearance({
        material: new Material({
          fabric,
        })
      }),
      asynchronous: false, // 确定图元是异步创建还是阻塞直到准备就绪
      compressVertices: false, // 关闭顶点压缩
    });
  }else{
    return new Primitive({
      geometryInstances: geometry,
      appearance: new EllipsoidSurfaceAppearance({
        material: new Material({
          fabric,
        })
      }),
      asynchronous: false, // 确定图元是异步创建还是阻塞直到准备就绪
      compressVertices: false, // 关闭顶点压缩
    });
  }
}

function rangeFormat(range) {
  return [range[0], 255 * range[1] + range[0], range[1]];
}

function createColorTexture(range = [], colors, options) {
  const interpolateColor = colors.map((item) => ({
    key: item[0],
    value: item[1],
  }));
  const keys = interpolateColor.map((d) => parseFloat(d.key));
  const [min, max] = [
    range[0] || Math.min(...keys),
    range[1] || Math.max(...keys),
  ];
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = 256;
  canvas.height = 1;

  if (ctx) {
    if (options.colorScaleType !== 'linear') {
      const gradient = ctx.createLinearGradient(0, 0, 256, 0);

      for (let i = 0; i < interpolateColor.length; i += 1) {
        const key = interpolateColor[i].key;
        const color = interpolateColor[i].value;
        gradient.addColorStop((key - min) / (max - min), color);
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 256, 1);
    } else {
      for (let i = 0; i < interpolateColor.length; i += 1) {
        const key = interpolateColor[i].key;
        let keyNext = key;
        if (i < interpolateColor.length - 1) {
          keyNext = interpolateColor[i + 1].key;
        } else {
          keyNext = max;
        }
        const color = interpolateColor[i].value;
        const current = ((key - min) / (max - min)) * 256; // 0 - 256
        const next = ((keyNext - min) / (max - min)) * 256; // 0 - 256
        ctx.fillStyle = color;
        ctx.fillRect(current, 0, next - current, 1);
      }
    }

    // const data = new Uint8Array(ctx.getImageData(0, 0, 256, 1).data)

    return {
      canvas,
      colorRange: [min, max],
    };
  }
}
